import React, { useState, useEffect, useMemo } from "react"
import { Card, Modal } from "reactstrap"
import Loader from "pages/Separate/Loader"
import { fetchNodeData } from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"

const PASS_SCORE = 70
const EXCELLENCE_SCORE = 90

function scoreColor(s) {
  if (s >= EXCELLENCE_SCORE) return "#16a34a"
  if (s >= PASS_SCORE) return "#d97706"
  return "#dc2626"
}
function scoreBg(s) {
  if (s >= EXCELLENCE_SCORE) return "#f0fdf4"
  if (s >= PASS_SCORE) return "#fffbeb"
  return "#fff5f5"
}
function scoreBorder(s) {
  if (s >= EXCELLENCE_SCORE) return "#86efac"
  if (s >= PASS_SCORE) return "#fde68a"
  return "#fecaca"
}
function scoreLeftBorder(s) {
  if (s >= EXCELLENCE_SCORE) return "#22c55e"
  if (s >= PASS_SCORE) return "#f59e0b"
  return "#ef4444"
}
function ringColor(s) {
  if (s >= EXCELLENCE_SCORE) return "#27ae60"
  if (s >= PASS_SCORE) return "#f39c12"
  return "#e74c3c"
}

function ScoreRing({ score, label }) {
  return (
    <div style={{ textAlign: "center", minWidth: 64 }}>
      <div style={{
        width: 60, height: 60, borderRadius: "50%", margin: "0 auto 4px",
        background: `conic-gradient(${ringColor(score)} ${score * 3.6}deg, #e9d5ff 0deg)`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: "50%", background: "#faf5ff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 13, color: scoreColor(score),
        }}>{score}%</div>
      </div>
      <div style={{ fontSize: 11, color: "#752a97", fontWeight: 600 }}>{label}</div>
    </div>
  )
}

const ExternalCandidates = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [search, setSearch] = useState("")
  const [filterTest, setFilterTest] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [filterViolations, setFilterViolations] = useState("")
  const [modal, setModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [activeDetailTab, setActiveDetailTab] = useState("overview")

  useEffect(() => {
    const load = async () => {
      const token = secureLocalStorage.getItem("auth_token")
      const res = await fetchNodeData(setLoading, "preassesment/data/external", token)
      if (res?.success) setData(res.data || [])
    }
    load()
  }, [])

  const testOptions = useMemo(() => [...new Set(data.map(d => d.test_name))].sort(), [data])

  const filtered = useMemo(() => {
    return data.filter(row => {
      const q = search.toLowerCase()
      if (q && !row.name?.toLowerCase().includes(q) && !row.email?.toLowerCase().includes(q)) return false
      if (filterTest && row.test_name !== filterTest) return false
      if (filterStatus === "pass" && row.score < PASS_SCORE) return false
      if (filterStatus === "fail" && row.score >= PASS_SCORE) return false
      if (filterStatus === "excellent" && row.score < EXCELLENCE_SCORE) return false
      if (filterViolations === "yes" && row.violation_count === 0) return false
      if (filterViolations === "no" && row.violation_count > 0) return false
      return true
    })
  }, [data, search, filterTest, filterStatus, filterViolations])

  const stats = useMemo(() => ({
    total: filtered.length,
    pass: filtered.filter(r => r.score >= PASS_SCORE).length,
    fail: filtered.filter(r => r.score < PASS_SCORE).length,
    avg: filtered.length ? Math.round(filtered.reduce((s, r) => s + r.score, 0) / filtered.length) : 0,
    violations: filtered.filter(r => r.violation_count > 0).length,
  }), [filtered])

  const openDetail = (row) => {
    setSelected(row)
    setActiveDetailTab("overview")
    setModal(true)
  }

  const hasFilters = search || filterTest || filterStatus || filterViolations

  return (
    <div>
      {isLoading && <Loader />}
      <Card>
        <div className="card-body">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-3 pb-2"
            style={{ borderBottom: "1.5px solid #f0e6f8" }}>
            <div className="d-flex align-items-center gap-2">
              <div style={{
                width: 34, height: 34, borderRadius: 8,
                background: "linear-gradient(135deg, #752a97, #a855c9)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <i className="fas fa-user-tie text-white" style={{ fontSize: 15 }}></i>
              </div>
              <span className="text-purple fw-bold" style={{ fontSize: 15 }}>External Candidates</span>
            </div>
            {data.length > 0 && (
              <span style={{ background: "#f3e8ff", color: "#752a97", border: "1px solid #d8b4fe", borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 600 }}>
                {data.length} Response{data.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Stats + Filters in one row */}
          <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
            {/* Stats */}
            {data.length > 0 && (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {[
                  { label: "Total", value: stats.total, color: "#752a97", bg: "#f5eeff", icon: "fas fa-users" },
                  { label: "Passed", value: stats.pass, color: "#16a34a", bg: "#edfbf3", icon: "fas fa-check-circle" },
                  { label: "Failed", value: stats.fail, color: "#dc2626", bg: "#fff5f5", icon: "fas fa-times-circle" },
                ].map(({ label, value, color, bg, icon }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: `1.5px solid ${color}25`, borderRadius: 10, padding: "6px 12px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className={icon} style={{ color, fontSize: 11 }}></i>
                    </div>
                    <div style={{ lineHeight: 1.2 }}>
                      <div style={{ fontSize: 10, color: "#888", fontWeight: 500 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color }}>{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="position-relative" style={{ flex: "1 1 160px", maxWidth: 200 }}>
              <i className="fas fa-search position-absolute" style={{ left: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa", fontSize: 12 }}></i>
              <input
                className="form-control"
                style={{ paddingLeft: 30, fontSize: 13, borderRadius: 8, height: 36 }}
                placeholder="Search name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="form-select border-radius" style={{ width: 150, fontSize: 13, height: 36 }}
              value={filterTest} onChange={e => setFilterTest(e.target.value)}>
              <option value="">All Tests</option>
              {testOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="form-select border-radius" style={{ width: 130, fontSize: 13, height: 36 }}
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="excellent">Excellent</option>
              <option value="pass">Pass</option>
              <option value="fail">Fail</option>
            </select>
            <select className="form-select border-radius" style={{ width: 150, fontSize: 13, height: 36 }}
              value={filterViolations} onChange={e => setFilterViolations(e.target.value)}>
              <option value="">All Violations</option>
              <option value="yes">Has Violations</option>
              <option value="no">No Violations</option>
            </select>
            {hasFilters && (
              <button className="btn btn-sm rounded-4 btn-red"
                onClick={() => { setSearch(""); setFilterTest(""); setFilterStatus(""); setFilterViolations("") }}>
                <i className="fas fa-times"></i>
              </button>
            )}
            </div>
          </div>

          {/* Table */}
          {filtered.length === 0 && !isLoading ? (
            <div className="text-center text-muted py-4">
              <i className="fas fa-user-tie fa-2x mb-2 d-block"></i>
              {hasFilters ? "No candidates match the current filters." : "No external candidate responses yet."}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Candidate</th>
                    <th>Test</th>
                    <th className="text-center" style={{ width: 90 }}>Score</th>
                    <th className="text-center" style={{ width: 110 }}>Status</th>
                    <th className="text-center" style={{ width: 110 }}>Violations</th>
                    <th style={{ width: 150 }}>Submitted On</th>
                    <th className="text-center" style={{ width: 80 }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row, i) => (
                    <tr key={row.id} style={{ background: scoreBg(row.score), borderLeft: `3px solid ${scoreLeftBorder(row.score)}` }}>
                      <td style={{ verticalAlign: "middle" }}>{i + 1}</td>
                      <td style={{ verticalAlign: "middle" }}>
                        <div style={{ fontWeight: 600, color: "#1f2937" }}>{row.name || "—"}</div>
                        <div style={{ fontSize: 11, color: "#6b7280" }}>{row.email}</div>
                      </td>
                      <td style={{ verticalAlign: "middle", fontWeight: 500 }}>{row.test_name}</td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: scoreColor(row.score) }}>{row.score}%</span>
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: scoreBg(row.score), border: `1px solid ${scoreBorder(row.score)}`, color: scoreColor(row.score) }}>
                          {row.score >= EXCELLENCE_SCORE ? <><i className="fas fa-star me-1"></i>Excellent</> : row.score >= PASS_SCORE ? <><i className="fas fa-check me-1"></i>Pass</> : <><i className="fas fa-times me-1"></i>Fail</>}
                        </span>
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        {row.violation_count > 0 ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#dc2626" }}>
                            <i className="fas fa-exclamation-triangle"></i> {row.violation_count}
                          </span>
                        ) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#16a34a" }}>
                            <i className="fas fa-shield-alt"></i> Clean
                          </span>
                        )}
                      </td>
                      <td style={{ verticalAlign: "middle", color: "#555" }}>
                        {moment(row.created_at).format("DD MMM YYYY, hh:mm A")}
                      </td>
                      <td className="text-center" style={{ verticalAlign: "middle" }}>
                        <button className="btn btn-sm" style={{ border: "1px solid #752a97", color: "#752a97", background: "transparent" }}
                          onClick={() => openDetail(row)}>
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Detail Modal */}
      {selected && (
        <Modal isOpen={modal} size="xl" centered toggle={() => setModal(false)}>
          <div className="modal-header pe-4">
            <div>
              <h5 className="modal-title mt-0">
                <i className="fas fa-user-tie me-2 text-purple"></i>
                {selected.name || selected.email}
              </h5>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {selected.email} &nbsp;·&nbsp; {selected.test_name} &nbsp;·&nbsp;
                {moment(selected.created_at).format("DD MMM YYYY, hh:mm A")}
              </div>
            </div>
            <button type="button" onClick={() => setModal(false)} className="btn-modal-close">
              <span aria-hidden="true" className="btn-modal-span">&times;</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="d-flex gap-0" style={{ borderBottom: "1px solid #f0e6f8", padding: "0 20px" }}>
            {[
              { key: "overview", label: "Overview", icon: "fas fa-chart-pie" },
              { key: "answers", label: `Answers (${selected.answers?.length || 0})`, icon: "fas fa-list-ol" },
              { key: "violations", label: `Violations (${selected.violation_count})`, icon: "fas fa-exclamation-triangle" },
            ].map(tab => (
              <button key={tab.key} type="button"
                onClick={() => setActiveDetailTab(tab.key)}
                style={{
                  padding: "10px 18px", border: "none", background: "transparent", cursor: "pointer",
                  fontSize: 13, fontWeight: 600,
                  color: activeDetailTab === tab.key ? "#752a97" : "#9ca3af",
                  borderBottom: activeDetailTab === tab.key ? "2px solid #752a97" : "2px solid transparent",
                  marginBottom: -1,
                }}>
                <i className={`${tab.icon} me-1`}></i>{tab.label}
              </button>
            ))}
          </div>

          <div className="modal-body p-4" style={{ maxHeight: "65vh", overflowY: "auto" }}>
            {/* Overview tab */}
            {activeDetailTab === "overview" && (
              <div>
                <div className="d-flex align-items-center gap-3 p-3 mb-3"
                  style={{ background: "#faf5ff", borderRadius: 10, border: "1px solid #e9d5ff" }}>
                  <ScoreRing score={selected.score} label="Score" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "#1f2937", marginBottom: 6 }}>{selected.test_name}</div>
                    <div className="d-flex flex-wrap gap-3" style={{ fontSize: 12, color: "#555" }}>
                      <span><i className="fas fa-user me-1 text-purple"></i>{selected.name || "—"}</span>
                      <span><i className="fas fa-envelope me-1 text-purple"></i>{selected.email}</span>
                      <span><i className="fas fa-calendar me-1 text-purple"></i>{moment(selected.created_at).format("DD MMM YYYY, hh:mm A")}</span>
                      <span>
                        <i className="fas fa-exclamation-triangle me-1" style={{ color: selected.violation_count > 0 ? "#dc2626" : "#16a34a" }}></i>
                        {selected.violation_count} Violation{selected.violation_count !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="mt-2">
                      <span style={{ display: "inline-block", padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: scoreBg(selected.score), border: `1px solid ${scoreBorder(selected.score)}`, color: scoreColor(selected.score) }}>
                        {selected.score >= EXCELLENCE_SCORE ? "Excellent" : selected.score >= PASS_SCORE ? "Passed" : "Failed"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Answers tab */}
            {activeDetailTab === "answers" && (
              <div>
                {(!selected.answers || selected.answers.length === 0) ? (
                  <div className="text-center text-muted py-4">No answers recorded.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {selected.answers.map((ans, idx) => {
                      const qText = ans.question?.question || `Question ${idx + 1}`
                      const qType = ans.question?.type
                      const isCorrect = ans.isCorrect
                      const userAns = ans.user_answer ?? "—"
                      const correctAns = ans.correct_answer ?? "—"
                      const options = (() => {
                        try { return ans.question?.options ? JSON.parse(ans.question.options) : null } catch { return null }
                      })()

                      return (
                        <div key={idx} style={{ border: `1px solid ${isCorrect === true ? "#86efac" : isCorrect === false ? "#fecaca" : "#e9d5ff"}`, borderLeft: `4px solid ${isCorrect === true ? "#22c55e" : isCorrect === false ? "#ef4444" : "#a78bfa"}`, borderRadius: 8, padding: "12px 14px", background: isCorrect === true ? "#f0fdf4" : isCorrect === false ? "#fff5f5" : "#faf5ff" }}>
                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                            <div style={{ fontWeight: 600, fontSize: 13, color: "#1f2937", flex: 1 }}>
                              <span style={{ color: "#752a97", marginRight: 6 }}>Q{idx + 1}.</span>{qText}
                            </div>
                            {isCorrect === true && <span style={{ fontSize: 11, fontWeight: 600, color: "#16a34a", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 20, padding: "2px 8px", whiteSpace: "nowrap" }}><i className="fas fa-check me-1"></i>Correct</span>}
                            {isCorrect === false && <span style={{ fontSize: 11, fontWeight: 600, color: "#dc2626", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 20, padding: "2px 8px", whiteSpace: "nowrap" }}><i className="fas fa-times me-1"></i>Wrong</span>}
                          </div>
                          {qType === 1 && options && (
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {Object.entries(options).map(([k, v]) => {
                                const isUserPick = String(userAns) === String(k)
                                const isCorrectPick = String(correctAns) === String(k)
                                return (
                                  <span key={k} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, border: `1px solid ${isCorrectPick ? "#86efac" : isUserPick ? "#fca5a5" : "#e5e7eb"}`, background: isCorrectPick ? "#f0fdf4" : isUserPick ? "#fee2e2" : "#f9fafb", color: isCorrectPick ? "#15803d" : isUserPick ? "#b91c1c" : "#6b7280", fontWeight: isUserPick || isCorrectPick ? 600 : 400 }}>
                                    {k}. {v}{isUserPick && !isCorrectPick && " ✗"}{isCorrectPick && " ✓"}
                                  </span>
                                )
                              })}
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: "#555" }}>
                            <span style={{ fontWeight: 600, color: "#374151" }}>Their answer: </span>
                            <span style={{ color: isCorrect === false ? "#dc2626" : "#374151" }}>{String(userAns)}</span>
                          </div>
                          {isCorrect === false && (
                            <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>
                              <span style={{ fontWeight: 600 }}>Correct: </span>{String(correctAns)}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Violations tab */}
            {activeDetailTab === "violations" && (
              <div>
                {selected.violation_count === 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-shield-alt fa-2x mb-2 d-block" style={{ color: "#22c55e" }}></i>
                    <div style={{ fontWeight: 600, color: "#16a34a" }}>No violations detected</div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>This candidate completed the test without any integrity issues.</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {selected.violations.map((v, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 14px", borderRadius: 8, background: "#fff5f5", border: "1px solid #fecaca", borderLeft: "4px solid #ef4444" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <i className="fas fa-exclamation-triangle" style={{ color: "#dc2626", fontSize: 11 }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#1f2937", textTransform: "capitalize" }}>
                            {(v.type || v.event || "Violation").replace(/_/g, " ")}
                          </div>
                          {v.timestamp || v.occurred_at || v.time ? (
                            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                              <i className="fas fa-clock me-1"></i>
                              {moment(v.timestamp || v.occurred_at || v.time).format("hh:mm:ss A")}
                            </div>
                          ) : null}
                          {v.description && <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{v.description}</div>}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 20, padding: "2px 8px" }}>#{idx + 1}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ExternalCandidates
